/**
 * Pure Ohm's Law laboratory physics helpers.
 * SI units are used throughout: volts, amperes, ohms, metres, and square metres.
 */

export interface MeterConfiguration {
  /** Peak magnitude of uniformly distributed reading noise, in the measured unit. */
  noiseAmplitude?: number;
  /** Display resolution; readings are rounded to the nearest increment. */
  resolution?: number;
  /** Absolute full-scale range. A reading beyond it is marked over-range. */
  range?: number;
  /** Injected random source for deterministic tests and reproducible sessions. */
  random?: () => number;
}

export interface MeterReading {
  trueValue: number;
  noisyValue: number;
  reading: number | null;
  noise: number;
  overRange: boolean;
}

export interface OhmsLawCircuitInput {
  supplyVoltage: number;
  resistance: number;
  /** Additional series resistance (for example, a rheostat ahead of the test wire). */
  seriesResistance?: number;
  /** Supply internal resistance; defaults to a small realistic 0.5 Ω. */
  internalResistance?: number;
  /** Ammeter shunt and lead resistance; defaults to 0.02 Ω. */
  ammeterResistance?: number;
  /** Voltmeter input resistance; defaults to 10 MΩ. */
  voltmeterResistance?: number;
  ammeterRange?: number;
  voltmeterRange?: number;
  voltageNoiseAmplitude?: number;
  currentNoiseAmplitude?: number;
  voltageResolution?: number;
  currentResolution?: number;
  random?: () => number;
}

export interface OhmsLawCircuitResult {
  supplyVoltage: number;
  resistance: number;
  seriesResistance: number;
  equivalentVoltmeterLoad: number;
  totalCircuitResistance: number;
  sourceCurrent: number;
  resistorCurrent: number;
  voltmeterCurrent: number;
  resistorVoltage: number;
  ammeter: MeterReading;
  voltmeter: MeterReading;
}

export interface DataPoint {
  x: number;
  y: number;
}

export interface LinearFit {
  slope: number;
  intercept: number;
  rSquared: number;
}

function assertFiniteNonNegative(name: string, value: number): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a finite, non-negative number`);
  }
}

function assertResistance(name: string, value: number, allowInfinity = false): void {
  if ((allowInfinity && value === Number.POSITIVE_INFINITY) || (Number.isFinite(value) && value > 0)) return;
  throw new RangeError(`${name} must be positive${allowInfinity ? ' or Infinity' : ''}`);
}

/** Calculate the equivalent resistance of positive series resistors. */
export function seriesResistance(resistances: number[]): number {
  if (resistances.length === 0) throw new RangeError('At least one resistance is required');
  resistances.forEach((resistance, index) => assertResistance(`resistances[${index}]`, resistance));
  return resistances.reduce((sum, resistance) => sum + resistance, 0);
}

/** Calculate equivalent resistance for positive parallel branches. */
export function parallelResistance(resistances: number[]): number {
  if (resistances.length === 0) throw new RangeError('At least one resistance is required');
  resistances.forEach((resistance, index) => assertResistance(`resistances[${index}]`, resistance, true));
  const conductance = resistances.reduce((sum, resistance) => sum + (Number.isFinite(resistance) ? 1 / resistance : 0), 0);
  if (conductance === 0) return Number.POSITIVE_INFINITY;
  return 1 / conductance;
}

/**
 * Compute an analog instrument reading with bounded, injectable noise and
 * finite display resolution. The ideal circuit value is never mutated.
 */
export function measureWithMeter(value: number, configuration: MeterConfiguration = {}): MeterReading {
  if (!Number.isFinite(value)) throw new RangeError('Meter value must be finite');
  const noiseAmplitude = configuration.noiseAmplitude ?? 0;
  const resolution = configuration.resolution ?? 0;
  const range = configuration.range ?? Number.POSITIVE_INFINITY;
  const random = configuration.random ?? Math.random;
  assertFiniteNonNegative('noiseAmplitude', noiseAmplitude);
  if (resolution !== 0) assertResistance('resolution', resolution);
  assertResistance('range', range, true);

  const randomValue = random();
  if (!Number.isFinite(randomValue) || randomValue < 0 || randomValue > 1) {
    throw new RangeError('random source must return a number between 0 and 1');
  }
  const noise = (randomValue * 2 - 1) * noiseAmplitude;
  const noisyValue = value + noise;
  const overRange = Math.abs(noisyValue) > range;
  const reading = overRange
    ? null
    : resolution > 0
      ? Math.round(noisyValue / resolution) * resolution
      : noisyValue;

  return { trueValue: value, noisyValue, reading, noise, overRange };
}

/**
 * Solve a DC circuit containing a source, its internal resistance, a series
 * ammeter, and a voltmeter connected in parallel with the measured resistor.
 * Ammeter current includes the tiny current drawn by the voltmeter branch.
 */
export function solveOhmsLawCircuit(input: OhmsLawCircuitInput): OhmsLawCircuitResult {
  assertFiniteNonNegative('supplyVoltage', input.supplyVoltage);
  assertResistance('resistance', input.resistance);
  const internalResistance = input.internalResistance ?? 0.5;
  const ammeterResistance = input.ammeterResistance ?? 0.02;
  const seriesResistanceValue = input.seriesResistance ?? 0;
  const voltmeterResistance = input.voltmeterResistance ?? 10_000_000;
  assertFiniteNonNegative('internalResistance', internalResistance);
  assertFiniteNonNegative('ammeterResistance', ammeterResistance);
  assertFiniteNonNegative('seriesResistance', seriesResistanceValue);
  assertResistance('voltmeterResistance', voltmeterResistance, true);

  const equivalentVoltmeterLoad = parallelResistance([input.resistance, voltmeterResistance]);
  const totalCircuitResistance = internalResistance + ammeterResistance + seriesResistanceValue + equivalentVoltmeterLoad;
  const sourceCurrent = input.supplyVoltage / totalCircuitResistance;
  const resistorVoltage = sourceCurrent * equivalentVoltmeterLoad;
  const resistorCurrent = resistorVoltage / input.resistance;
  const voltmeterCurrent = Number.isFinite(voltmeterResistance) ? resistorVoltage / voltmeterResistance : 0;

  return {
    supplyVoltage: input.supplyVoltage,
    resistance: input.resistance,
    seriesResistance: seriesResistanceValue,
    equivalentVoltmeterLoad,
    totalCircuitResistance,
    sourceCurrent,
    resistorCurrent,
    voltmeterCurrent,
    resistorVoltage,
    ammeter: measureWithMeter(sourceCurrent, {
      noiseAmplitude: input.currentNoiseAmplitude ?? 0,
      resolution: input.currentResolution ?? 0,
      range: input.ammeterRange ?? Number.POSITIVE_INFINITY,
      random: input.random,
    }),
    voltmeter: measureWithMeter(resistorVoltage, {
      noiseAmplitude: input.voltageNoiseAmplitude ?? 0,
      resolution: input.voltageResolution ?? 0,
      range: input.voltmeterRange ?? Number.POSITIVE_INFINITY,
      random: input.random,
    }),
  };
}

/** Resistance of a uniform wire: R = ρL/A. Area is supplied in mm². */
export function resistanceFromWireLength(
  resistivityOhmMetres: number,
  lengthMetres: number,
  crossSectionMm2: number,
): number {
  assertResistance('resistivityOhmMetres', resistivityOhmMetres);
  assertResistance('lengthMetres', lengthMetres);
  assertResistance('crossSectionMm2', crossSectionMm2);
  const areaSquareMetres = crossSectionMm2 * 1e-6;
  return (resistivityOhmMetres * lengthMetres) / areaSquareMetres;
}

/** Ordinary least-squares fit for graphing recorded laboratory readings. */
export function fitStraightLine(points: DataPoint[]): LinearFit {
  if (points.length < 2) throw new RangeError('At least two points are required for a linear fit');
  points.forEach((point, index) => {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      throw new RangeError(`Point ${index} must have finite x and y values`);
    }
  });
  const meanX = points.reduce((sum, point) => sum + point.x, 0) / points.length;
  const meanY = points.reduce((sum, point) => sum + point.y, 0) / points.length;
  const varianceX = points.reduce((sum, point) => sum + (point.x - meanX) ** 2, 0);
  if (varianceX === 0) throw new RangeError('Cannot fit a line when all x values are identical');
  const covariance = points.reduce((sum, point) => sum + (point.x - meanX) * (point.y - meanY), 0);
  const slope = covariance / varianceX;
  const intercept = meanY - slope * meanX;
  const totalVarianceY = points.reduce((sum, point) => sum + (point.y - meanY) ** 2, 0);
  const residualVariance = points.reduce((sum, point) => sum + (point.y - (slope * point.x + intercept)) ** 2, 0);
  const rSquared = totalVarianceY === 0 ? 1 : 1 - residualVariance / totalVarianceY;
  return { slope, intercept, rSquared };
}
